import {
    Component,
    EventEmitter,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import OverlayScrollbars from "overlayscrollbars";
import { NavigationService } from "src/app/navigation/navigation.service";
import { AngularAssignToolComponent } from "src/app/reusables/assign-tool/angular-assign-tool/angular-assign-tool.component";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { YouTubeInfoService } from "src/app/reusables/services/youtube-info.service";
import tinymce, { Editor } from "tinymce";
import { WorkAidsService } from "../work-aids.service";
import { IframeAvailability } from "src/shared/models/broadcasts.model";

@Component({
    selector: "cv-work-aids-new",
    templateUrl: "./work-aids-new.component.html",
    styleUrls: ["./work-aids-new.component.scss"],
})
export class WorkAidsNewComponent implements OnInit {
    @ViewChild(AngularAssignToolComponent)
    angularAssignTool: AngularAssignToolComponent;
    @Output() assignListRemove = new EventEmitter<any>();
    public addMode: boolean = true;
    public editMode: boolean = false;
    public reuseMode: boolean = false;
    public showAssign: boolean = false;
    public showLive: boolean = false;
    public saving: boolean = false;
    public loadingRecipients: boolean = false;
    public hyperlinkPopupOpen: boolean = false;
    public videoPlayerOpen: boolean = false;
    public videoPopupOpen: boolean = false;
    public editConfirmationOpen: boolean = false;
    public validatingIframe: boolean = false;
    public iframeValidated: boolean = false;
    public skipNextIframeValidation: boolean = false;
    public previewFailed: boolean = false;

    public hyperlinkTextTemp: string = "";
    public hyperlinkAddressTemp: string = "";
    public iframeAvailability: IframeAvailability;
    public videoLink: string = "";
    public videoTempList = [];
    public videoInfoTempList = [];
    public folders = null;
    public allTags = [];

    public player: any = null;
    public dateFormat = CVAngularDateFormat;
    public placeholderLocalized: string;
    public hyperlinkTooltip: string;

    public groups = [];

    public errorMessages = {
        recipients: null,
        name: null,
        body: null,
        bodyMaxLength: null,
        folder: null,
        hyperlinkText: null,
        hyperlinkAddress: null,
        video: null,
        date: null,
        tags: null,
    };

    public scrollOptions: OverlayScrollbars.PartialOptions = {
        scrollbars: {
            autoHide: "leave",
            autoHideDelay: 100,
            theme: "os-theme-dark",
            visibility: "auto",
        },
    };

    constructor(
        private sanitizer: DomSanitizer,
        private cvLocalizationService: CVLocalizationService,
        public service: WorkAidsService,
        private youTubeService: YouTubeInfoService,
        private navigationService: NavigationService
    ) {}

    async ngOnInit() {
        await this.cvLocalizationService.GetSection("Broadcasts");

        this.hyperlinkTooltip = this.cvLocalizationService.getCachedText("Broadcasts", "Preview_Warning");

        this.service.currentModes.subscribe((modes) => {
            this.addMode = modes.addMode;
            this.editMode = modes.editMode;
            this.reuseMode = modes.reuseMode;
            this.setGroups();
        });

        this.folders = this.service.folders;
        this.allTags = this.service.allTags;
        this.placeholderLocalized = this.cvLocalizationService.getCachedText(
            "Broadcasts",
            "Select_Date",
            "Select Date"
        );
    }

    cancel() {
        this.service.save = {
            BroadcastID: null,
            Title: "",
            BroadcastText: "",
            Folder: "",
            Urgent: false,
            ExpirationDate: null,
            Quiz: null,
            OriginalQuiz: null,
            Recipients: [],
            RecipientAssignments: [],
            Attachments: [],
            Images: [],
            ImagesData: [],
            Hyperlinks: [],
            Videos: [],
            VideoInfo: [],
            Tags: null,
            MinorEdit: false,
        };
        tinymce.activeEditor.execCommand("mceSetContent", false, "");
        this.errorMessages = {
            name: null,
            body: null,
            bodyMaxLength: null,
            folder: null,
            hyperlinkText: null,
            hyperlinkAddress: null,
            video: null,
            date: null,
            tags: null,
            recipients: null,
        };
        this.showLive = true;
        this.hyperlinkTextTemp = "";
        this.hyperlinkAddressTemp = "";
        this.iframeAvailability = 0;
        this.iframeValidated = false;
        this.validatingIframe = false;
        this.previewFailed = false;
        this.changeMode(false, false, false);
    }

    async reset() {
        const response = await this.service.loadWorkAids();

        if (response) {
            this.service.progressPopupOpen = false;
            const workAidList = response.broadcasts;
            this.folders = response.folders;
            this.allTags = response.allTags;

            this.service.workAidList = workAidList;
            this.service.folders = this.folders;
            this.service.allTags = this.allTags;

            const workAidVideoMapping = [];
            const videoIds = [];

            for (let i = 0; i < this.service.workAidList.length; i++) {
                this.service.workAidList[i].VideoInfo = [];
                const hyperlinkResponses = [];

                this.service.workAidList[i].Videos.forEach((video) => {
                    workAidVideoMapping.push(i);
                    videoIds.push(video);
                });

                if (this.service.workAidList[i].Videos.length > 0) {
                    const youtubeResponse =
                        await this.youTubeService.getYouTubeInformation(
                            this.service.workAidList[i].Videos
                        );

                    youtubeResponse?.items.forEach((item) => {
                        this.service.workAidList[i].VideoInfo.push(
                            item.snippet
                        );
                    });
                }
                const hyperlinksPromises = [];
                if(this.service.workAidList[i]?.Hyperlinks.length > 0){
                    for(let j =0 ; j < this.service.workAidList[i].Hyperlinks.length; j++){
                        hyperlinksPromises.push(this.service.GetIFrameAvailability(this.service.workAidList[i].Hyperlinks[j].HyperlinkAddress));
                    }

                    const hyperlinksResponses = await Promise.all(hyperlinksPromises);
                    hyperlinksResponses.forEach((response, index) => {
                        this.service.workAidList[i].Hyperlinks[index].ContentSecurityPolicyViolation = response.contentSecurityPolicyViolation;
                        this.service.workAidList[i].Hyperlinks[index].IframeAvailability = response.responseCode;
                    });
                }
            }

            this.saving = false;
            this.cancel();
        } else {
            this.service.progressPopupOpen = false;
        }
    }

    noRecipientsSet() {
        return angular.equals(this.service.save.RecipientAssignments, []);
    }

    removeRecipient(recipient) {
        const index = this.service.save.RecipientAssignments.findIndex((r) =>
            angular.equals(r, recipient)
        );

        if (index >= 0) this.service.save.RecipientAssignments.splice(index, 1);
        this.setGroups();
        const assignObj = recipient.group;
        this.angularAssignTool.removeEvent(assignObj); // Have to manually trigger the remove event in order to differentiate between retained groups and the deletion
        this.validateAll();
    }

    validateAll() {
        this.validateRecipients();
        this.validateName();
        this.validateBody();
        this.validateFolder();
        this.validateDate();
        this.validateTags();
        return this.noErrorMessages();
    }

    validateRecipients() {
        if (this.service.save.RecipientAssignments.length <= 0)
            this.errorMessages.recipients =
                this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Recipients_Required"
                );
        else this.errorMessages.recipients = null;
    }

    validateName() {
        if (this.service.save.Title.length <= 0)
            this.errorMessages.name = this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Name_Required"
            );
        else if (this.service.save.Title.length > 36)
            this.errorMessages.name = this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Name_Characters"
            );
        else this.errorMessages.name = null;
    }

    validateBody() {
        this.errorMessages.body =
            this.service.save.BroadcastText.length <= 0
                ? this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Body_Required"
                )
                : null;

        const textBody = tinymce.activeEditor.getContent({format: "text"});
        this.errorMessages.bodyMaxLength =
            textBody.length > 500
                ? this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Broadcast_Text_Max_Length",
                    "Maximum of 500 characters for the message."
                )
                : null;
    }

    validateFolder() {
        if (this.service.save.Folder.length <= 0)
            this.errorMessages.folder =
                this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Folder_Required"
                );
        else if (this.service.save.Folder.length > 28)
            this.errorMessages.folder =
                this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Folder_Characters"
                );
        else this.errorMessages.folder = null;
    }

    validateDate() {
        if (
            this.service.save.ExpirationDate &&
            new Date(this.service.save.ExpirationDate).getTime() < Date.now()
        )
            this.errorMessages.date = this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Invalid_Expiration_Date"
            );
        else this.errorMessages.date = null;
    }

    validateTags() {
        this.errorMessages.tags = null;
        if (this.service.save.TagsList == null || this.service.save.TagsList == "")
            return;
        const tags = this.service.save.TagsList.split(",");
        const tooLongTag = tags.find((tag) => tag.trim().length > 30);
        this.errorMessages.tags = tooLongTag
            ? this.cvLocalizationService.getCachedText(
                "Broadcasts",
                "Tag_Characters"
            )
            : null;
    }

    validateHyperlinkText() {
        this.errorMessages.hyperlinkText =
            this.hyperlinkTextTemp.length <= 0
                ? this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Hyperlink_Text_Required"
                )
                : null;
    }

    validateHyperlinkAddress() {
        if (this.hyperlinkAddressTemp.length <= 0)
            this.errorMessages.hyperlinkAddress =
                this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Hyperlink_Address_Required"
                );
        else {
            this.errorMessages.hyperlinkAddress = null;
        }
    }

    async validateIframe() {
        if(this.validatingIframe || this.skipNextIframeValidation){
            this.skipNextIframeValidation = false;
            return;
        }
        this.iframeValidated = false;
        if(this.hyperlinkAddressTemp.length <= 0){
            this.validatingIframe = false;
            this.previewFailed= false;
            return;
        }
        this.validatingIframe = true;

        let response = null;
        response = await this.service.GetIFrameAvailability(this.hyperlinkAddressTemp);
        if(response){
            this.previewFailed = response.contentSecurityPolicyViolation || response.responseCode == 3;
            if(response.outputAddress != this.hyperlinkAddressTemp){
                this.hyperlinkAddressTemp = response.outputAddress;
                this.skipNextIframeValidation = true;
            }
        }
        this.iframeAvailability = this.hyperlinkAddressTemp.startsWith("http:") && response.responseCode == 0 ? 4 : 
            response.responseCode == 3 ? 0 : response?.responseCode;   // The backend considers 3 an error, but Rejected means we will have no preview which is not an error with the link
        
        this.validatingIframe = false;
        this.iframeValidated = true;
    }

    validateVideo(videoId) {
        this.errorMessages.video =
            videoId == null || videoId.length != 11
                ? this.cvLocalizationService.getCachedText(
                    "Broadcasts",
                    "Video_Link_Invalid"
                )
                : null;
    }

    pastePreprocess(plugin: Editor, args){
        const splitContent = args.content.split('<br />');
        const cleanedContent = [];
        splitContent.forEach((line) => {
            cleanedContent.push("<div>" + line + "</div>");
        });
        args.content = cleanedContent.join('');
    }

    noErrorMessages() {
        const errorMessages = Object.values(this.errorMessages);
        for (let i = 0; i < errorMessages.length; i++) {
            if (errorMessages[i] != null) return false;
        }
        return true;
    }

    openVideoPopup() {
        this.videoTempList = this.service.save.Videos.slice(0);
        this.videoInfoTempList = this.service.save.VideoInfo.slice(0);
        this.videoLink = "";
        this.videoPopupOpen = true;
    }

    openPlayer(videoId) {
        this.videoPlayerOpen = true;
        if (this.player != null) {
            if (this.player.playerInfo.videoData.video_id != videoId)
                // Deletes the player so it can be refreshed if a different video has been selected
                this.player.destroy();
            // Same video has been selected, don't mess with the player at all
            else return;
        }

        // Including the time out allows the Youtube IFrame API to get ready
        setTimeout(() => {
            this.player = new (<any>window).YT.Player("VideoPlayer", {
                height: "100%",
                width: "100%",
                videoId: videoId,
                playerVars: {
                    modestbranding: 1, //minimizes the amount of youtube brand flair in the player
                    playsinline: 0, //sets the playback to be in fullscreen mode (required for iOS)
                    rel: 0, //stops related videos from being shown when the video end
                    host: "https://dev.clearviewportal.com", //this is set as a security feature otherwise a warning about xss will frequently appear in the console
                },
            });
        }, 50);
    }

    async addVideo() {
        // Ensure that video link is valid
        const videoId = this.videoLink.match("[\\?&]v=([^&#]*)");
        this.validateVideo(videoId != null ? videoId[1] : null);
        if (this.errorMessages.video == null) {
            // Add video link to list if it isn't in already, clear link entry box
            if (!this.videoTempList.includes(videoId[1])) {
                this.videoTempList.push(videoId[1]);
                const youtubeResponse =
                    await this.youTubeService.getYouTubeInformation(videoId[1]);

                if (youtubeResponse) {
                    this.videoInfoTempList.push(
                        youtubeResponse.items[0].snippet
                    );
                }
            }
            this.videoLink = "";
        }
    }

    videoPlayerClose() {
        this.videoPlayerOpen = false;
        this.player.pauseVideo();
    }

    videoPopupApply() {
        this.service.save.Videos = this.videoTempList;
        this.service.save.VideoInfo = this.videoInfoTempList;
        this.videoPopupOpen = false;
    }

    addHyperlink() {
        // Ensure that URL is valid
        this.validateHyperlinkText();
        this.validateHyperlinkAddress();
        if (
            this.errorMessages.hyperlinkText == null &&
            this.errorMessages.hyperlinkAddress == null
        ) {
            // Add hyperlink to list, close hyperlink entry popup
            this.service.save.Hyperlinks.push({
                HyperlinkAddress: this.hyperlinkAddressTemp,
                HyperlinkText: this.hyperlinkTextTemp,
                ContentSecurityPolicyViolation: this.previewFailed,
                IframeAvailability: this.iframeAvailability,
            });
            this.closeHyperlinkPopup();
        }
    }

    closeHyperlinkPopup() {
        this.hyperlinkTextTemp = "";
        this.hyperlinkAddressTemp = "";
        this.hyperlinkPopupOpen = false;
        this.validatingIframe = false;
        this.iframeAvailability = 0;
        this.iframeValidated = false;
        this.previewFailed = false;
    }

    attachmentPicked(event, isImage: boolean = false) {
        const files = event.target;
        let fileNameArray = files.value.split("\\");
        const fileName = fileNameArray[fileNameArray.length - 1];
        fileNameArray = fileName.split(".");

        let attachmentName = "";
        for (let i = 0; i < fileNameArray.length - 1; i++) {
            attachmentName += fileNameArray[i] + ".";
        }
        attachmentName = attachmentName.slice(0, -1);

        let extension = fileNameArray[fileNameArray.length - 1];
        if (extension == "html" && isImage) return;
        const wholeFile = files.files[0];
        let fileContents = null;
        const fileByteArray = [];
        let fileString = "";

        const reader = new FileReader();
        reader.onload = (evt) => {
            fileContents = evt.target.result;
            let array = new Uint8Array(fileContents);
            for (let i = 0; i < array.length; i++) {
                fileByteArray.push(array[i]);
                fileString += String.fromCharCode(array[i]);
            }

            let fileStringConverted = window.btoa(fileString);
            if (isImage) {
                this.service.save.Images.push({
                    AltText: attachmentName,
                    FileExtension: extension,
                    FileString: fileStringConverted,
                });
                this.service.save.ImagesData.push(fileByteArray);
            } else {
                this.service.save.Attachments.push({
                    AttachmentName: attachmentName,
                    AttachmentExtension: extension,
                    FileData: fileByteArray,
                });
            }
        };
        reader.readAsArrayBuffer(wholeFile);
    }

    removeAttachment(type, index) {
        switch (type) {
            case 1:
                this.service.save.Attachments.splice(index, 1);
                break;
            case 2:
                this.service.save.Images.splice(index, 1);
                this.service.save.ImagesData.splice(index, 1);
                break;
            case 3:
                this.service.save.Hyperlinks.splice(index, 1);
                break;
            case 4:
                this.service.save.Videos.splice(index, 1);
                this.service.save.VideoInfo.splice(index, 1);
                break;
        }
    }

    async saveNew() {
        if (this.saving) return;
        this.service.save.BroadcastText = tinymce.activeEditor.getContent();
        if (this.validateAll()) {
            this.saving = true;
            this.service.progressPopupOpen = true;
            const response = await this.service.AddNewWorkAid();
            if (response) this.reset();
        }
    }

    async saveEdit() {
        if (this.saving) return;
        this.service.save.BroadcastText = tinymce.activeEditor.getContent();
        if (this.validateAll()) {
            this.saving = true;
            this.editConfirmationOpen = false;
            this.service.progressPopupOpen = true;
            const response = await this.service.EditWorkAid();

            if (response) this.reset();
        }
    }

    async getQuizzes() {
        if (this.service.quizzes == null) {
            const response = await this.service.GetAccessibleQuizzes()
            
            if(response){
                this.service.quizzes = response;
            }
        }
    }

    openQuizPreview() {
        if (this.service.save.Quiz != null) {
            this.service.save.FormID = +this.service.save.Quiz;
            this.service.recordingId = 0; 
            this.service.quizPreviewEditable = true;
            this.service.quizPopupOpen = true;
            this.service.quizPreviewShowScore = false;
        }
    }

    quizChanged(event) {
        this.service.save.Quiz = event.selectedIndex == 0 ? null : event.value;
        this.service.save.FormID = this.service.save.Quiz;
        if (this.service.save.Quiz != this.service.save.OriginalQuiz && this.service.save.Quiz != null) {
            this.service.save.MinorEdit = false;
        }
    }

    assignToolToggle(b: boolean) {
        if (!b) {
            // when assign tool closes, validate Recipients
            this.setGroups();
            this.validateRecipients();
        }
    }

    setGroups() {
        const groups = [];
        this.service.save.RecipientAssignments.forEach((recipient) => {
            groups.push({ ...recipient.group });
        });
        this.groups = groups;
    }

    changeMode(
        addMode: boolean = false,
        editMode: boolean = false,
        reuseMode: boolean = false
    ) {
        this.addMode = addMode;
        this.editMode = editMode;
        this.reuseMode = reuseMode;
        this.service.changeMode(addMode, editMode, reuseMode);
        this.setGroups();
    }

    openEditConfirmation() {
        if (this.validateAll()) this.editConfirmationOpen = true;
    }

    tinyMceLanguage() {
        const loggedInLang = this.navigationService.loggedInLang;
        switch (loggedInLang) {
            case "Français":
            case "Français Canadien":
                return "fr";
            case "Español":
                return "es";
            case "Deutsch":
                return "de";
            case "Portugues do Brasil":
                return "pt_BR";
            case "Japanese":
                return "ja";
            case "Dutch":
                return "nl";
            default:
                return undefined;
        }
    }
}
